// src/services/entitiesService.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************

import { buildQueryFromObject, returnOnlyNumbers } from 'utils/helpers';
import axiosService from './axiosService';
import { API } from 'constants/Api';
import { mergeUrlParamsQuery } from 'utils/urlUtils';
import { ENTITY_PROFILE_COLORS } from 'constants/EntityConstants';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// *******************************************************************************
// SERVICE METHODS
// *******************************************************************************

const decorator = (data, dataBefore, ctx) => {
  if (!data) return data;

  // Clone data to avoid mutations
  const transformed = { ...data };

  // ───────────────────────────────────────────────────────────────────────────
  // EMAIL TRANSFORMATION
  // Transform email object back to array format for backend
  // Add _id from original data to identify which contact to update
  // ───────────────────────────────────────────────────────────────────────────
  if (data.email && typeof data.email === 'object') {
    // Get original email contact from contacts array (has _id, etc)
    const originalEmailContact =
      dataBefore?.contacts?.find(c => c.contactType === 'email' && c.isPrimary) ||
      dataBefore?.contacts?.find(c => c.contactType === 'email');

    // Ensure _id is included in the email object for edition
    transformed.email = {
      ...data.email,
      _id: originalEmailContact?._id,
    };
  }

  // ───────────────────────────────────────────────────────────────────────────
  // PHONE TRANSFORMATION
  // Transform phone object back to array format for backend
  // Add _id from original data to identify which contact to update
  // ───────────────────────────────────────────────────────────────────────────
  if (data.phone && typeof data.phone === 'object') {
    // Get original phone contact from contacts array (has _id, countryCode, etc)
    const originalPhoneContact =
      dataBefore?.contacts?.find(c => c.contactType === 'mobile' && c.isPrimary) ||
      dataBefore?.contacts?.find(c => c.contactType === 'mobile') ||
      dataBefore?.contacts?.find(c => c.contactType === 'phone');

    // Ensure _id is included in the phone object for edition
    transformed.phone = {
      ...data.phone,
      value: returnOnlyNumbers(data.phone?.value),
      _id: originalPhoneContact?._id,
      countryCode: data?.phone?.countryCode || originalPhoneContact?.countryCode,
    };
  }

  // ───────────────────────────────────────────────────────────────────────────
  // ADDRESS TRANSFORMATION
  // Transform address object back to array format for backend
  // Add _id and preserve countryCode from original data
  // ───────────────────────────────────────────────────────────────────────────
  if (data.address && typeof data.address === 'object') {
    // Get original address from addresses array (has _id, countryCode, etc)
    // NOTE: addressType can be string ('main') or array (['main']), handle both cases
    const originalAddress =
      dataBefore?.addresses?.find(a => {
        const type = a.addressType;
        const isMain = type === 'main' || (Array.isArray(type) && type.includes('main'));
        return isMain && a.isPrimary;
      }) ||
      dataBefore?.addresses?.find(a => {
        const type = a.addressType;
        return type === 'main' || (Array.isArray(type) && type.includes('main'));
      }) ||
      dataBefore?.addresses?.[0];

    // Determine countryCode from multiple sources
    const addressCountryCode =
      data.address?.countryCode ||
      data.address?.addressMetadata?.countryCode ||
      originalAddress?.countryCode ||
      originalAddress?.addressMetadata?.countryCode ||
      data?.countryCode ||
      dataBefore?.countryCode ||
      'BR';

    // Ensure _id and countryCode are included in the address object for edition
    // countryCode is set during CEP search and must be preserved
    // Also ensure addressMetadata.countryCode is set (required by backend)
    transformed.address = {
      ...data.address,
      _id: originalAddress?._id,
      countryCode: addressCountryCode,
      // Preserve and fix addressMetadata
      addressMetadata: {
        ...originalAddress?.addressMetadata,
        ...data.address?.addressMetadata,
        countryCode: addressCountryCode, // Ensure countryCode is always set
      },
    };
  }

  // ───────────────────────────────────────────────────────────────────────────
  // DOCUMENTS TRANSFORMATION
  // Transform document objects (cpf, rg, cnpj, etc.) back to their format
  // Add _id from original data to identify which document to update
  // ───────────────────────────────────────────────────────────────────────────
  // Common document types to check
  const documentTypes = ['cpf', 'cnpj', 'rg', 'passport', 'cnh', 'cei', 'crc', 'crm'];

  documentTypes.forEach(docType => {
    if (data[docType] && typeof data[docType] === 'object') {
      // Get original document from documents array (has _id, etc)
      const originalDocument = dataBefore?.documents?.find(d => d.documentType === docType);

      // Priority: data > originalDocument > data root > dataBefore root > 'BR'
      const countryCode =
        data[docType]?.countryCode ||
        originalDocument?.countryCode ||
        data?.countryCode ||
        dataBefore?.countryCode ||
        'BR';

      // Priority for value: data > originalDocument
      const value = data[docType]?.value || originalDocument?.value;

      // Ensure _id, documentType, countryCode, and value are included for edition
      // These are required fields in the backend schema
      transformed[docType] = {
        ...data[docType],
        _id: originalDocument?._id,
        documentType: docType,
        countryCode,
        value,
      };
    }
  });

  // ───────────────────────────────────────────────────────────────────────────
  // ATTACHMENTS TRANSFORMATION
  // Transform attachment objects (avatar, logo, cover, etc.) back to their format
  // Add _id from original data to identify which attachment to update
  // ───────────────────────────────────────────────────────────────────────────
  const attachmentTypes = [
    'avatar',
    'logo',
    'cover',
    'gallery',
    'document',
    'contract',
    'qrcode',
    'signature',
    'other',
  ];

  attachmentTypes.forEach(attachType => {
    if (data[attachType] && typeof data[attachType] === 'object') {
      // Get original attachment from attachments array (has _id, etc)
      const originalAttachment = dataBefore?.attachments?.find(
        a => a.attachmentType === attachType
      );

      // Ensure _id is included in the attachment object for edition
      transformed[attachType] = {
        ...data[attachType],
        _id: originalAttachment?._id,
      };
    }
  });

  // ───────────────────────────────────────────────────────────────────────────
  // ATTACHMENTS ARRAY PRESERVATION
  // getChangedFields() partial rebuild may omit attachmentId when only other
  // fields in attachments[N] changed. Restore attachmentId and _id from
  // dataBefore (storedCurrentPage.data) which always has the full original data.
  // ───────────────────────────────────────────────────────────────────────────
  if (Array.isArray(data.attachments)) {
    transformed.attachments = data.attachments.map(att => {
      const original = dataBefore?.attachments?.find(
        o => o.attachmentType === att.attachmentType || o._id === att._id
      );
      return {
        ...att,
        attachmentId: att.attachmentId || original?.attachmentId,
        _id: att._id || original?._id,
      };
    });
  }

  return transformed;
};

const getEntitiesByField = async ({ returnAsObject = false, fullSearch = false, ...rest }) => {
  /*
  const {
    folder,
    entityType,
    entityProfile,
    field,
    value,
    project, // Array of string (ex: ['name', 'birthdate', ...])
    returnAsObject = false,
    fullSearch = false, // false: use Regex to find by part of name, true: find 'full name' without regex.
  } = rest;
*/

  let url = `${API.ENTITIES.GET_BY_FIELD}?${buildQueryFromObject(rest)}`;

  const axiosOptions = {
    url,
    returnAsObject,
    headers: { 'x-fullsearch': fullSearch },
  };

  const [docs] = await axiosService.get(axiosOptions);

  return docs;
};

const getEntityByField = async (value, field = 'cpfCnpj', project) => {
  const options = {
    returnAsObject: true,
    fullSearch: true,
    field: field,
    value: value,
    project,
  };

  return await getEntitiesByField(options);
};

const getEntityProfileColor = entityProfile => {
  return ENTITY_PROFILE_COLORS?.[entityProfile] || ENTITY_PROFILE_COLORS.fallback;
};

/**
 * Search for entity by document (CNPJ, CPF, etc.) within current subscription
 * Used for unique document validation to prevent duplicates
 *
 * @param {Object} options - Search options
 * @param {string} options.key - Document type (e.g., 'cnpj', 'cpf')
 * @param {string} options.value - Document value to search
 * @returns {Promise<Object>} Response with found entity or null
 *
 * @example
 * const result = await entitiesService.searchByDocument({
 *   key: 'cnpj',
 *   value: '02934311000153'
 * });
 * if (result.found) {
 *   console.log('Entity found:', result.data);
 * }
 */
const searchByDocument = async ({ key, value }) => {
  try {
    const url = `${API.ENTITIES.SEARCH_BY_DOCUMENT}?key=${key}&value=${returnOnlyNumbers(value)}`;

    const [data] = await axiosService.get({ url, returnAsObject: true });

    return data;

    // return {
    //   success: data?.success ?? false,
    //   found: data?.found ?? false,
    //   data: data?.data ?? null,
    // };
  } catch (error) {
    console.error('[entitiesService.searchByDocument] Error:', error);
    return null;

    // return {
    //   success: false,
    //   found: false,
    //   data: null,
    //   error: error.message || 'Error searching entity by document',
    // };
  }
};

/**
 * Add, update, or remove items from an array field in an entity document
 * Uses MongoDB $push/$pull operators via backend add-to-set route
 *
 * @param {Object} options - Options object
 * @param {string} options._id - Entity document ID
 * @param {string} options.operator - MongoDB operator: '$push' (add/edit) or '$pull' (remove)
 * @param {string} options.path - Array field path (e.g., 'contacts', 'addresses')
 * @param {Object} options.data - Data to add/update (for $push) or subdocumentId/_id to remove (for $pull)
 * @returns {Promise<Object>} Updated entity document
 *
 * @example
 * // Add new contact
 * await entitiesService.addToSet({
 *   _id: 'entityId',
 *   operator: '$push',
 *   path: 'contacts',
 *   data: { contactType: 'email', value: 'test@test.com', isPrimary: true }
 * });
 *
 * @example
 * // Update existing contact (include subdocumentId or _id in data)
 * await entitiesService.addToSet({
 *   _id: 'entityId',
 *   operator: '$push',
 *   path: 'contacts',
 *   data: { _id: 'contactId', contactType: 'email', value: 'new@test.com' }
 * });
 *
 * @example
 * // Remove contact by ID
 * await entitiesService.addToSet({
 *   _id: 'entityId',
 *   operator: '$pull',
 *   path: 'contacts',
 *   data: { subdocumentId: 'contactId' }
 * });
 */
const addToSet = async ({ _id, operator, path, data }) => {
  const url = mergeUrlParamsQuery({
    url: API.ENTITIES.ADD_TO_SET,
    params: { _id, operator, path },
  });

  const axiosOptions = {
    url,
    data,
    headers: { 'x-resultasobject': true },
  };

  const [result] = await axiosService.put(axiosOptions);
  return result;
};

// *******************************************************************************
// RESPONSE DECORATOR
// Transforms entity arrays (documents[], contacts[], addresses[]) into flat
// form-friendly fields (cnpj, email, phone, address fields at root).
// Mirror of decorator() which does the inverse transformation on save.
// *******************************************************************************

const decodeResponse = data => {
  if (!data) return data;

  const decoded = { ...data };

  // documents[] → cnpj (and other document types)
  if (Array.isArray(data.documents)) {
    const documentTypes = ['cpf', 'cnpj', 'rg', 'passport', 'cnh', 'cei', 'crc', 'crm'];
    documentTypes.forEach(docType => {
      const doc = data.documents.find(d => d.documentType === docType);
      if (doc) decoded[docType] = doc;
    });
  }

  // contacts[] → email, phone
  if (Array.isArray(data.contacts)) {
    const emailContact =
      data.contacts.find(c => c.contactType === 'email' && c.isPrimary) ||
      data.contacts.find(c => c.contactType === 'email');
    if (emailContact) decoded.email = emailContact;

    const phoneContact =
      data.contacts.find(c => c.contactType === 'mobile' && c.isPrimary) ||
      data.contacts.find(c => c.contactType === 'mobile') ||
      data.contacts.find(c => c.contactType === 'phone');
    if (phoneContact) decoded.phone = phoneContact;
  }

  // addresses[] → root-level fields (for formatAddress/setFoundZip compatibility)
  // AND nested decoded.address (SimpleAddressFormBR uses subdocumentField="address"
  // → registers fields as ['address','zip'], ['address','address1'], etc.)
  if (Array.isArray(data.addresses) && data.addresses.length > 0) {
    const mainAddress =
      data.addresses.find(a => {
        const type = a.addressType;
        return type === 'main' || (Array.isArray(type) && type.includes('main'));
      }) || data.addresses[0];

    if (mainAddress) {
      // Root-level: needed by formatAddress() → setFoundZip() for zip state initialization
      decoded.address1 = mainAddress.address1;
      decoded.address2 = mainAddress.address2;
      decoded.number = mainAddress.number;
      decoded.neighborhood = mainAddress.neighborhood;
      decoded.city = mainAddress.city;
      decoded.region = mainAddress.region;
      decoded.zip = mainAddress.zip;
      decoded.__city = mainAddress.__city;
      decoded.addressCoordinates = mainAddress.addressCoordinates;
      decoded.addressAdditionalInfo = mainAddress.addressAdditionalInfo;
      decoded.addressMetadata = mainAddress.addressMetadata;
      decoded.addressTitle = mainAddress.addressTitle;

      // Nested: form.setFieldsValue({ address: { zip, address1, ... } }) populates
      // ['address','zip'], ['address','address1'], etc. correctly
      // __city fallback: CNPJ auto-fill saves city/region but not __city (ZIP-search field);
      // derive __city so the display field is not blank on reload
      const derivedCity =
        mainAddress.__city ||
        (mainAddress.city && mainAddress.region
          ? `${mainAddress.city}/${mainAddress.region}`
          : mainAddress.city);
      decoded.address = {
        address1: mainAddress.address1,
        address2: mainAddress.address2,
        number: mainAddress.number,
        neighborhood: mainAddress.neighborhood,
        city: mainAddress.city,
        region: mainAddress.region,
        zip: mainAddress.zip,
        __city: derivedCity,
        addressCoordinates: mainAddress.addressCoordinates,
        addressAdditionalInfo: mainAddress.addressAdditionalInfo,
        addressMetadata: mainAddress.addressMetadata,
        addressTitle: mainAddress.addressTitle,
      };
    }
  }

  return decoded;
};

// *******************************************************************************
// EXPORTS
// *******************************************************************************

export default {
  decorator,
  decodeResponse,
  getEntitiesByField,
  getEntityByField,
  getEntityProfileColor,
  searchByDocument,
  addToSet,
};

// *******************************************************************************
// HELPERS
// *******************************************************************************
